import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';

import { StudentService } from './services/student.service';
import {
  DtoIntervention,
  DtoInterventionEffectiveness,
  DtoStudentAITimeline,
  DtoTimelineEvent,
} from './models/dto-student-ai-timeline';

type LoadState = 'loading' | 'ready' | 'error';

export type SeverityLevel = 'severe' | 'moderate' | 'mild' | 'neutral' | 'positive' | 'very-positive';

interface TimelineEventView extends DtoTimelineEvent {
  severity: SeverityLevel;
  severityLabel: string;
  severityScoreLabel: string;
}

interface InterventionView extends DtoIntervention {
  effectivenessLabel: string;
  linkedEventCount: number;
}

const SEVERITY_LABELS: Record<SeverityLevel, string> = {
  severe: 'Severe',
  moderate: 'Moderate',
  mild: 'Mild',
  neutral: 'Neutral',
  positive: 'Positive',
  'very-positive': 'Very Positive',
};

const EFFECTIVENESS_LABELS: Record<DtoInterventionEffectiveness, string> = {
  minimum: 'Minimum Effectiveness',
  medium: 'Medium Effectiveness',
  high: 'High Effectiveness',
};

export function toSeverityLevel(score: number): SeverityLevel {
  if (score <= -3) return 'severe';
  if (score === -2) return 'moderate';
  if (score === -1) return 'mild';
  if (score >= 3) return 'very-positive';
  if (score >= 1) return 'positive';
  return 'neutral';
}

@Component({
  selector: 'app-student-ai-timeline',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './student-ai-timeline.component.html',
  styleUrls: ['./student-ai-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudentAiTimelineComponent implements OnChanges {
  private static instanceCount = 0;

  private readonly studentService = inject(StudentService);
  private request?: Subscription;

  /** The student whose AI timeline is shown. */
  @Input({ required: true }) studentId!: string;

  /** Emits the student id when "Student View" is clicked, so the portal can navigate. */
  @Output() readonly studentViewSelected = new EventEmitter<string>();

  readonly uid = `student-ai-timeline-${StudentAiTimelineComponent.instanceCount++}`;

  readonly severityKey: { level: SeverityLevel; label: string }[] = [
    { level: 'severe', label: 'Severe (-5 to -3)' },
    { level: 'moderate', label: 'Moderate (-2)' },
    { level: 'mild', label: 'Mild (-1)' },
    { level: 'positive', label: 'Positive (+1 to +2)' },
    { level: 'very-positive', label: 'Very Positive (+3 to +5)' },
  ];

  readonly skeletonCards = [0, 1, 2];

  readonly loadState = signal<LoadState>('loading');
  readonly selectedInterventionId = signal<number | null>(null);
  private readonly timeline = signal<DtoStudentAITimeline | null>(null);

  readonly student = computed(() => this.timeline()?.student ?? null);

  readonly initials = computed(() =>
    (this.student()?.fullName ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join(''),
  );

  readonly summaryParagraphs = computed(() => this.timeline()?.aiSummary?.paragraphs ?? []);

  readonly events = computed<TimelineEventView[]>(() =>
    [...(this.timeline()?.timelineEvents ?? [])]
      .sort((a, b) => b.eventDate.localeCompare(a.eventDate) || b.eventId - a.eventId)
      .map((event) => {
        const severity = toSeverityLevel(event.severityScore);
        return {
          ...event,
          severity,
          severityLabel: SEVERITY_LABELS[severity],
          severityScoreLabel: `${event.severityScore > 0 ? '+' : ''}${event.severityScore}`,
        };
      }),
  );

  readonly interventions = computed<InterventionView[]>(() => {
    const data = this.timeline();
    if (!data) return [];
    return [...data.interventions]
      .sort((a, b) => b.startDate.localeCompare(a.startDate))
      .map((intervention) => ({
        ...intervention,
        effectivenessLabel: EFFECTIVENESS_LABELS[intervention.effectiveness] ?? '',
        linkedEventCount: data.timelineEvents.filter((e) => e.interventionId === intervention.interventionId)
          .length,
      }));
  });

  readonly selectedInterventionName = computed(() => {
    const id = this.selectedInterventionId();
    return id === null ? null : (this.interventions().find((i) => i.interventionId === id)?.name ?? null);
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.request?.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentId']) {
      this.load();
    }
  }

  load(): void {
    this.request?.unsubscribe();
    this.selectedInterventionId.set(null);
    this.loadState.set('loading');

    this.request = this.studentService.getAITimeline(this.studentId).subscribe({
      next: (data) => {
        this.timeline.set(data);
        this.loadState.set('ready');
      },
      error: () => this.loadState.set('error'),
    });
  }

  toggleIntervention(interventionId: number): void {
    this.selectedInterventionId.update((current) => (current === interventionId ? null : interventionId));
  }

  clearSelection(): void {
    this.selectedInterventionId.set(null);
  }

  isDimmed(event: TimelineEventView): boolean {
    const selected = this.selectedInterventionId();
    return selected !== null && event.interventionId !== selected;
  }

  openStudentView(): void {
    this.studentViewSelected.emit(this.studentId);
  }
}
